import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Send, X, MessageCircleHeart, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';
import { generateChatResponse, generateTakeaway, getWelcomeMessage } from '../lib/groq';
import EndChatModal from '../components/EndChatModal';
import AuthModal from '../components/AuthModal';
import MessageLimitModal from '../components/MessageLimitModal';
import ReflectionCreatedModal from '../components/ReflectionCreatedModal';
import LifetimeAccessModal from '../components/LifetimeAccessModal';
import type { Message, Profile } from '../lib/supabase-types';
import { saveActiveSession, getActiveSession, clearActiveSession } from '../lib/chat-storage';

const MESSAGE_LIMIT = 4;
const FREE_MESSAGE_LIMIT = 3;
const STORED_TAKEAWAY_KEY = 'stored_takeaway';
const STORED_MESSAGES_KEY = 'stored_messages';

const ChatPage = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [previousHistory, setPreviousHistory] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEndChatModal, setShowEndChatModal] = useState(false);
  const [showMessageLimitModal, setShowMessageLimitModal] = useState(false);
  const [showReflectionCreatedModal, setShowReflectionCreatedModal] = useState(false);
  const [showLifetimeModal, setShowLifetimeModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [isGeneratingReflection, setIsGeneratingReflection] = useState(false);
  const [totalUserMessages, setTotalUserMessages] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    if (chatContainerRef.current) {
      const scrollHeight = chatContainerRef.current.scrollHeight;
      chatContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior,
      });
    }
  };

  useLayoutEffect(() => {
    if (isFirstRender.current && messages.length > 0) {
      scrollToBottom();
      isFirstRender.current = false;
    }
  }, [messages]);

  useEffect(() => {
    if (!isFirstRender.current) {
      scrollToBottom('smooth');
    }
  }, [messages]);

  useEffect(() => {
    if (sessionId && messages.length > 0) {
      saveActiveSession(sessionId, messages, !isAuthenticated);
    }
  }, [messages, sessionId, isAuthenticated]);

  const createNewSession = async (userId: string | null = null) => {
    try {
      const { data: session, error: sessionError } = await supabase
        .from('chat_sessions')
        .insert({
          is_anonymous: !userId,
          user_id: userId
        })
        .select()
        .single();

      if (sessionError) throw sessionError;
      if (!session) throw new Error('No session created');

      console.log('[Debug] Created new session:', session.id);
      setSessionId(session.id);

      const welcomeContent = await getWelcomeMessage();
      const welcomeMessage: Message = {
        id: Date.now().toString(),
        content: welcomeContent,
        sender: 'bot',
        session_id: session.id,
        created_at: new Date().toISOString(),
      };
      
      setMessages([welcomeMessage]);

      await supabase.from('messages').insert({
        session_id: session.id,
        content: welcomeMessage.content,
        sender: 'bot',
        user_id: userId
      });

      // Save the session immediately after creation for anonymous users
      if (!userId) {
        saveActiveSession(session.id, [welcomeMessage], true);
      }

      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    } catch (error) {
      console.error('[Debug] Error creating new session:', error);
      setError('Unable to start chat session. Please try again.');
    }
  };

  useEffect(() => {
    const initializeChat = async () => {
      try {
        console.log('[Debug] Initializing chat...');

        // Always clear any existing session on page load to start fresh
        clearActiveSession();

        const { data: { user } } = await supabase.auth.getUser();
        setIsAuthenticated(!!user);

        if (user) {
          console.log('[Debug] User is authenticated:', user.id);
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (profile) {
            setUserProfile(profile);
            await Promise.all([
              fetchChatHistory(user.id),
              fetchTotalUserMessages(user.id)
            ]);
          }

          // Create new session for authenticated user
          await createNewSession(user.id);
        } else {
          // Create new anonymous session
          await createNewSession();
        }
      } catch (error) {
        console.error('[Debug] Error in initializeChat:', error);
        setError('Unable to start chat session. Please try again.');
      }
    };

    initializeChat();
  }, [navigate]);

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);

    if (sessionId) {
      try {
        console.log('[Debug] Handling auth success...');
        setIsGeneratingReflection(true);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No user found');

        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (!profile) {
          throw new Error('Profile not found');
        }

        console.log('[Debug] Converting anonymous session to authenticated...');

        const { error: sessionUpdateError } = await supabase
          .from('chat_sessions')
          .update({
            user_id: user.id,
            is_anonymous: false
          })
          .eq('id', sessionId);

        if (sessionUpdateError) {
          console.error('[Debug] Error updating session:', sessionUpdateError);
          throw sessionUpdateError;
        }

        const { error: messagesUpdateError } = await supabase
          .from('messages')
          .update({ user_id: user.id })
          .eq('session_id', sessionId)
          .is('user_id', null);

        if (messagesUpdateError) {
          console.error('[Debug] Error updating messages:', messagesUpdateError);
        }

        setIsAuthenticated(true);
        setUserProfile(profile);

        console.log('[Debug] Generating takeaway with', messages.length, 'messages');
        console.log('[Debug] Messages:', messages.map(m => ({ sender: m.sender, content: m.content.substring(0, 50) })));

        const takeaway = await generateTakeaway(messages);
        console.log('[Debug] Generated takeaway successfully:', takeaway.substring(0, 100));

        console.log('[Debug] Inserting reflection into database...');
        const { data: insertedReflection, error: insertError } = await supabase.from('reflections').insert({
          user_id: user.id,
          session_id: sessionId,
          content: takeaway
        }).select();

        if (insertError) {
          console.error('[Debug] Error saving reflection:', insertError);
          throw new Error('Failed to save reflection: ' + insertError.message);
        }

        console.log('[Debug] Reflection saved successfully:', insertedReflection);

        clearActiveSession();
        setIsGeneratingReflection(false);
        setShowReflectionCreatedModal(true);

      } catch (error) {
        console.error('[Debug] Error in handleAuthSuccess:', error);
        setIsGeneratingReflection(false);
        const errorMessage = error instanceof Error ? error.message : 'Unable to save your reflection. Please try again.';
        setError(errorMessage);
        alert('Error creating reflection: ' + errorMessage);
      }
    }
  };

  const fetchTotalUserMessages = async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('authenticated_at')
        .eq('id', userId)
        .single();

      if (!profile?.authenticated_at) {
        return 0;
      }

      const { data: userMessages, error } = await supabase
        .from('messages')
        .select('id')
        .eq('sender', 'user')
        .eq('user_id', userId)
        .gte('created_at', profile.authenticated_at);

      if (error) throw error;

      setTotalUserMessages(userMessages?.length || 0);
      return userMessages?.length || 0;
    } catch (error) {
      console.error('Error fetching total user messages:', error);
      return 0;
    }
  };

  const checkMessageLimit = async (userId: string): Promise<boolean> => {
    try {
      if (!userId || userProfile?.has_lifetime_access) return true;

      const totalMessages = await fetchTotalUserMessages(userId);
      
      if (totalMessages >= FREE_MESSAGE_LIMIT) {
        setShowLifetimeModal(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking message limit:', error);
      return false;
    }
  };

  const fetchChatHistory = async (userId: string) => {
    try {
      const { data: sessions } = await supabase
        .from('chat_sessions')
        .select('id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(3);

      if (sessions && sessions.length > 0) {
        const { data: history } = await supabase
          .from('messages')
          .select('*')
          .in('session_id', sessions.map(s => s.id))
          .order('created_at', { ascending: true });

        if (history) {
          setPreviousHistory(history);
        }
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !sessionId || isGeneratingResponse) return;

    try {
      if (isAuthenticated && userProfile && !userProfile.has_lifetime_access) {
        const canContinue = await checkMessageLimit(userProfile.id);
        if (!canContinue) {
          return;
        }
      }

      if (!isAuthenticated && messages.filter(m => m.sender === 'user').length >= MESSAGE_LIMIT) {
        setShowMessageLimitModal(true);
        return;
      }

      console.log('[Debug] Sending message...');
      setError(null);
      const currentInput = input;
      setInput('');
      
      const newMessage: Message = {
        id: Date.now().toString(),
        content: currentInput,
        sender: 'user',
        session_id: sessionId,
        created_at: new Date().toISOString(),
      };

      setMessages(prev => [...prev, newMessage]);
      
      if (isAuthenticated && userProfile) {
        setTotalUserMessages(prev => prev + 1);
      }

      await supabase
        .from('messages')
        .insert({
          session_id: sessionId,
          content: currentInput,
          sender: 'user',
          user_id: userProfile?.id
        });

      setIsGeneratingResponse(true);
      const botResponse = await generateChatResponse([...messages, newMessage], previousHistory);
      setIsGeneratingResponse(false);

      const response: Message = {
        id: (Date.now() + 1).toString(),
        content: botResponse,
        sender: 'bot',
        session_id: sessionId,
        created_at: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, response]);

      await supabase
        .from('messages')
        .insert({
          session_id: sessionId,
          content: response.content,
          sender: 'bot',
          user_id: userProfile?.id
        });

      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error('[Debug] Error in handleSend:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      setError(errorMessage);
      setIsGeneratingResponse(false);

      const errorResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: errorMessage,
        sender: 'bot',
        session_id: sessionId,
        created_at: new Date().toISOString(),
      };

      setMessages(prev => [...prev, errorResponse]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEndChat = () => {
    setShowEndChatModal(true);
  };

  const handleUpgradeClick = async () => {
    if (isUpgrading) return;
    setIsUpgrading(true);

    try {
      console.log('[Stripe] Starting checkout process...');

      const { data: { session } } = await supabase.auth.getSession();
      console.log('[Stripe] Session check:', session ? 'Session found' : 'No session');

      if (!session?.access_token) {
        console.error('[Stripe] No access token available');
        throw new Error('Please sign in to upgrade your account');
      }

      console.log('[Stripe] Fetching checkout session from:', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
      });

      console.log('[Stripe] Checkout response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[Stripe] Checkout creation failed:', errorData);
        throw new Error(errorData.error || `Failed to create checkout session (${response.status})`);
      }

      const data = await response.json();
      console.log('[Stripe] Checkout data received:', data);

      if (!data.sessionId) {
        console.error('[Stripe] No session ID in response:', data);
        throw new Error('No session ID returned from checkout creation');
      }

      console.log('[Stripe] Loading Stripe.js with key:', import.meta.env.VITE_STRIPE_PUBLIC_KEY?.substring(0, 20) + '...');
      const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

      if (!stripe) {
        console.error('[Stripe] Failed to load Stripe.js');
        throw new Error('Failed to load Stripe. Please check your internet connection.');
      }

      console.log('[Stripe] Redirecting to checkout with session ID:', data.sessionId);
      const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });

      if (error) {
        console.error('[Stripe] Redirect error:', error);
        throw error;
      }
    } catch (error) {
      console.error('[Stripe] Complete error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to start checkout process. Please try again.';
      setError(errorMessage);
      alert(`Payment Error: ${errorMessage}\n\nPlease check the console for more details or contact support.`);
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-gradient-to-br from-purple-50/50 to-pink-50/50 fixed inset-0">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-purple-100/50 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center space-x-4">
          <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-2.5 rounded-2xl">
            <MessageCircleHeart className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold bg-gradient-to-r from-[#a85ff7] to-[#e457b5] bg-clip-text text-transparent">
              Chat with Echo
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Your safe space to talk</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleEndChat}
            className="group flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-red-600 rounded-xl transition-all duration-200 hover:bg-red-50"
          >
            <X className="h-5 w-5 transition-transform group-hover:rotate-90" />
            <span className="font-medium">End Chat</span>
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div 
        ref={chatContainerRef} 
        className="flex-1 overflow-y-auto momentum-scroll px-4 py-6"
      >
        <div className="max-w-2xl mx-auto space-y-6">
          <AnimatePresence mode="popLayout">
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                layout
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} group`}
              >
                {message.sender === 'bot' && (
                  <div className="w-10 h-10 mr-3 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0">
                    <MessageCircleHeart className="w-5 h-5 text-purple-600" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-br from-[#a85ff7] to-[#e457b5] text-white rounded-br-lg'
                      : 'bg-white text-gray-800 rounded-bl-lg shadow-sm border border-purple-100/50'
                  }`}
                >
                  <p className="leading-relaxed text-[15px]">{message.content}</p>
                </div>
              </motion.div>
            ))}
            {isGeneratingResponse && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex justify-start"
              >
                <div className="w-10 h-10 mr-3 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                  <MessageCircleHeart className="w-5 h-5 text-purple-600" />
                </div>
                <div className="bg-white rounded-2xl px-5 py-3.5 shadow-sm border border-purple-100/50">
                  <div className="flex items-center space-x-1.5">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Input Area */}
      <div className="sticky bottom-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-t border-purple-100/50">
        <div className="max-w-2xl mx-auto px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom)+4rem)]">
          <div className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Message Echo..."
                className="w-full px-5 py-3.5 bg-purple-50/50 border-2 border-purple-100/50 rounded-2xl placeholder-gray-400 focus:outline-none focus:border-purple-200 transition-colors text-[15px]"
                disabled={isGeneratingResponse}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={isGeneratingResponse || !input.trim()}
              className="p-3.5 bg-gradient-to-br from-[#a85ff7] to-[#e457b5] text-white rounded-xl hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex-shrink-0"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        mode="sign-up"
      />

      <EndChatModal
        isOpen={showEndChatModal}
        onClose={() => setShowEndChatModal(false)}
        sessionId={sessionId || ''}
        messages={messages.map(m => ({ sender: m.sender, content: m.content }))}
        isAuthenticated={isAuthenticated}
        onSignUpClick={() => {
          setShowEndChatModal(false);
          setShowAuthModal(true);
        }}
        onTakeawayGenerated={() => {
          clearActiveSession();
          navigate('/dashboard');
        }}
      />

      <MessageLimitModal
        isOpen={showMessageLimitModal}
        onClose={() => setShowMessageLimitModal(false)}
        onSignUpClick={() => {
          setShowMessageLimitModal(false);
          setShowAuthModal(true);
        }}
        messageCount={totalUserMessages}
      />

      <ReflectionCreatedModal
        isOpen={showReflectionCreatedModal}
        onClose={() => setShowReflectionCreatedModal(false)}
        onViewReflection={() => {
          clearActiveSession();
          navigate('/dashboard');
        }}
      />

      <LifetimeAccessModal
        isOpen={showLifetimeModal}
        onClose={() => setShowLifetimeModal(false)}
        onUpgradeClick={handleUpgradeClick}
        isUpgrading={isUpgrading}
      />

      {isGeneratingReflection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
                <MessageCircleHeart className="w-8 h-8 text-purple-600 animate-pulse" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Creating Your Reflection
                </h3>
                <p className="text-gray-600">
                  We're saving your conversation and generating a personalized reflection...
                </p>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;